import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assertAndContinue} from 'Asserts';

import {ToggleDisplayMode} from './Float';
import {FloatingPlacement, FloatingInteraction} from './FloatingConstants';
import Popover from './Popover';

export interface TooltipProps {
  children: JSX.Element;
  isDisabled?: boolean;
  placement?: FloatingPlacement;
  /** Tooltip text should be simple, if you have complex content Popover may be a more appropriate component
   * choice */
  text?: JSX.Element | string | null;
  content?: never;
  /** @see Float.tsx BaseFloatProps.toggleDisplayMode */
  toggleDisplayMode?: ToggleDisplayMode;
  disableContentSelection?: boolean;
  /**
   * ARIA role for the outermost floating element (i.e. the immediate child of the portal). If this float has
   * any interactivity within the floating content, this must be overridden.
   * @default tooltip
   */
  role?: string;
  /**
   * Time (in milliseconds) to wait to open/close the tooltip. See https://floating-ui.com/docs/usehover#delay
   * @default {open: 1000, close: 0}
   */
  delay?: {open: number; close: number};
  /**
   * Enable debug mode of the tooltip which keeps it open permanently
   */
  debug?: boolean;
}

function TooltipText({text}: {text?: string | null}): JSX.Element {
  return <div className={css(styles.tooltipText)}>{text}</div>;
}

export default function Tooltip({
  children,
  disableContentSelection,
  isDisabled,
  placement,
  role,
  text,
  toggleDisplayMode,
  delay = {open: 1000, close: 0},
  debug,
}: TooltipProps): JSX.Element {
  assertAndContinue(
    text !== '' || Boolean(isDisabled),
    'Tooltip text should not be an empty string. Use isDisabled or null value for text instead to disable.'
  );

  const tooltipContent = typeof text === 'string' ? <TooltipText text={text} /> : text;
  const disabled = isDisabled || text == null || text === '';
  let debugProps = {};
  if (debug) {
    debugProps = {
      interaction: FloatingInteraction.CONTROLLED,
      isOpen: true,
    };
  }

  return (
    <Popover
      placement={placement}
      renderContent={() => tooltipContent}
      isDisabled={disabled}
      toggleDisplayMode={toggleDisplayMode}
      returnFocusOnClose={false}
      role={role}
      disableContentSelection={disableContentSelection}
      delay={delay}
      dataTestAttrs={{component: 'Tooltip', element: 'content'}}
      {...debugProps}
    >
      {children}
    </Popover>
  );
}

export const styles = StyleSheet.create({
  tooltipText: {
    maxWidth: '200px',
    whiteSpace: 'pre-wrap',
  },
});
